/* eslint-disable max-lines -- TODO() */
import { formatShortDateTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert } from "@mui/material";
import { User } from "@sendbird/chat";
import Channel from "@sendbird/uikit-react/Channel";
import { SendableMessageType } from "@sendbird/uikit-react/types/utils";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { ChatPageWrapper } from "@src/appV2/Chat/ChatPageWrapper";
import { CHAT_MESSAGE_SPACING } from "@src/appV2/Chat/constants";
import { useWorkerForcedCancellationWarning } from "@src/appV2/Chat/hooks/useWorkerForcedCancellationWarning";
import { CHAT_LIST_FULL_PATH, WORKPLACE_GROUP_CHAT_PAGE_FULL_PATH } from "@src/appV2/Chat/paths";
import { GroupShiftInviteRequestMessage } from "@src/appV2/Chat/ShiftInviteResponse/GroupShiftInviteRequestMessage";
import { ShiftInviteRequestMessage } from "@src/appV2/Chat/ShiftInviteResponse/ShiftInviteRequestMessage";
import { ShiftTimeProposalMessage } from "@src/appV2/Chat/ShiftTimeProposalMessage/ShiftTimeProposalMessage";
import { CustomMessageTypes } from "@src/appV2/Chat/type";
import { getChannelShiftTime } from "@src/appV2/Chat/utils/getChannelShiftTime";
import { getSendHomeRequestMessage } from "@src/appV2/Chat/utils/getSendHomeRequestMessage";
import { VisitInviteRequestMessage } from "@src/appV2/Chat/VisitInviteRequest/VisitInviteRequestMessage";
import { WorkerCredentialSharedMessage } from "@src/appV2/Chat/WorkerCredentialMessage/WorkerCredentialSharedMessage";
import { WorkerVisitLockMessage } from "@src/appV2/Chat/WorkerVisitLock/WorkerVisitLockMessage";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useGetChartingNoncompliances } from "@src/appV2/redesign/Charting/api/useGetChartingNoncompliances";
import { useGetChatDetectedChartingCredentials } from "@src/appV2/redesign/Charting/api/useGetChatDetectedChartingCredentials";
import { ACTIVE_CHARTING_NONCOMPLIANCE_STATES } from "@src/appV2/redesign/Charting/constants";
import { DetectedChartingCredentialsCardContainer } from "@src/appV2/redesign/Charting/DetectedChartingCredentialsCardContainer";
import { useDetectCredentialKeywordInChat } from "@src/appV2/redesign/Charting/hooks/useDetectCredentialKeywordInChat";
import { BookedShiftMessage } from "@src/appV2/redesign/Chat/BookedShiftMessage";
import { ChatSeparator } from "@src/appV2/redesign/Chat/ChatSeparator";
import { MessageContentWithLongPress } from "@src/appV2/redesign/Chat/components/MessageContentWithLongPress";
import { EditMessageProvider } from "@src/appV2/redesign/Chat/context/EditMessageContext";
import { FlexChatHeader } from "@src/appV2/redesign/FlexChats/components/FlexChatHeader";
import { useGetWorkerVisitLockStatus } from "@src/appV2/redesign/InHome/useGetWorkerVisitLockStatus";
import { PageHeaderWrapper, Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { ChartingContextProvider } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { ReactElement } from "react";
import { useLocation, useParams, useRouteMatch } from "react-router-dom";

import { getMessageInputDefinition } from "./channel";
import { useChatChannelList } from "./ChatList/hooks/useChatChannelList";
import { useGetFacilityExclusion } from "./hooks/useGetFacilityExclusion";
import { hideMessage, isFacilityCanceledMeMessage } from "./utils/messageParsing";

const DETECT_CHARTING_CREDENTIALS_POLLING_INTERVAL_MS = 1000;

function getUserName(user: User): string {
  return user?.friendName || user?.nickname || user?.userId;
}

function getSenderName(message: SendableMessageType, isWorkplaceGroupChannel: boolean) {
  if (!isDefined(message.sender)) {
    return "";
  }
  const userName = getUserName(message.sender);

  // Only show first names in group chats
  return isWorkplaceGroupChannel ? userName.split(" ")[0] : userName;
}

export function ChatPage() {
  const isWorkplaceGroupChannel = isDefined(useRouteMatch(WORKPLACE_GROUP_CHAT_PAGE_FULL_PATH));

  const worker = useDefinedWorker();
  const { facilityId } = useParams<{ facilityId: string }>();
  const { search } = useLocation();
  const prefilledMessage = new URLSearchParams(search).get("prefilledMessage") ?? undefined;

  const { channels } = useChatChannelList({ skipWorkplaceFilter: true });

  const currentChannelUrl = isWorkplaceGroupChannel
    ? `${facilityId}_group`
    : facilityId + "_" + worker.userId;
  const currentChannel = channels.find((channel) => channel.url === currentChannelUrl);

  const exclusionQuery = useGetFacilityExclusion({ facilityId, agentId: worker.userId });

  const { data: lockStatusData } = useGetWorkerVisitLockStatus(worker.userId);
  const lockedAt = lockStatusData?.data?.attributes.lockedAt;

  const {
    data: chartingNoncompliances,
    isSuccess: isSuccessChartingNoncompliances,
    isLoading: isLoadingChartingNoncompliances,
  } = useGetChartingNoncompliances(
    { workerId: worker.userId, state: ACTIVE_CHARTING_NONCOMPLIANCE_STATES },
    { enabled: isDefined(worker.userId) && !isWorkplaceGroupChannel }
  );

  const detectedChartingCredentialsWidgetEnabled = useCbhFlag(
    CbhFeatureFlag.ROLLOUT_DETECTED_CHARTING_CREDENTIALS,
    {
      defaultValue: false,
    }
  );

  const { hasRecentlyDetectedCredentialsKeyword } = useDetectCredentialKeywordInChat({
    workerId: worker.userId,
    workplaceId: facilityId,
    enabled: detectedChartingCredentialsWidgetEnabled && !isWorkplaceGroupChannel,
  });

  const {
    data: chatDetectedChartingCredentials,
    isSuccess: isSuccessChatDetectedChartingCredentials,
    isLoading: isLoadingChatDetectedChartingCredentials,
  } = useGetChatDetectedChartingCredentials(
    { workerId: worker.userId, workplaceId: facilityId, status: "pending" },
    {
      enabled:
        isDefined(worker.userId) &&
        isDefined(facilityId) &&
        detectedChartingCredentialsWidgetEnabled &&
        !isWorkplaceGroupChannel,
      refetchInterval: (data) => {
        if (!hasRecentlyDetectedCredentialsKeyword) {
          return false;
        }

        const { length } = data?.data ?? [];
        if (length > 0) {
          // Stop polling if we already have detected credentials
          return false;
        }

        return DETECT_CHARTING_CREDENTIALS_POLLING_INTERVAL_MS;
      },
    }
  );

  const updateCredentialsModalState = useModalState();

  const hasPendingChatDetectedChartingCredentials =
    !isLoadingChatDetectedChartingCredentials &&
    isSuccessChatDetectedChartingCredentials &&
    chatDetectedChartingCredentials?.data.length > 0;

  const showDetectedChartingCredentialsWidget =
    detectedChartingCredentialsWidgetEnabled && hasPendingChatDetectedChartingCredentials;

  const { shouldShowWarning: shouldShowForcedCancellationWarning } =
    useWorkerForcedCancellationWarning(facilityId);

  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;

  const { facilityName } = currentChannel?.metadata || {};

  const workerHasChartingNonComplianceAtWorkplace =
    !isLoadingChartingNoncompliances &&
    isSuccessChartingNoncompliances &&
    chartingNoncompliances?.data.length > 0 &&
    chartingNoncompliances.data.some(
      (noncompliance) => noncompliance.relationships.workplace.data.id === facilityId
    );

  const header = () => (
    <>
      <PageHeaderWrapper variant="tertiary" sx={{ boxShadow: (theme) => theme.shadows[2] }}>
        <FlexChatHeader
          workplaceName={
            currentChannel?.shift?.facility?.name || facilityName || currentChannel?.name
          }
          channelShiftTime={getChannelShiftTime(currentChannel)}
          workplaceId={facilityId}
          fallbackGoBackPath={CHAT_LIST_FULL_PATH}
          isWorkplaceGroupChannel={isWorkplaceGroupChannel}
        />
      </PageHeaderWrapper>
      {shouldShowForcedCancellationWarning && !isWorkplaceGroupChannel && (
        <Alert
          severity="warning"
          variant="filled"
          icon={false}
          sx={{ borderRadius: 0, width: "100%" }}
        >
          Heads up! Asking a workplace to cancel is against our marketplace policy. Repeated reports
          can result in Clipboard Score penalties.
        </Alert>
      )}
      {showDetectedChartingCredentialsWidget && (
        <DetectedChartingCredentialsCardContainer
          workplaceId={facilityId ?? ""}
          updateCredentialsModalState={updateCredentialsModalState}
        />
      )}
    </>
  );

  return (
    <EditMessageProvider>
      <ChatPageWrapper isWorkplaceGroupChannel={isWorkplaceGroupChannel}>
        <Channel
          isReactionEnabled={false}
          renderChannelHeader={header}
          channelUrl={currentChannelUrl}
          renderCustomSeparator={({ message }) => {
            if (hideMessage(message)) {
              return <></>;
            }
            const createdAt = new Date(message.createdAt);

            return (
              <ChatSeparator sx={{ marginY: CHAT_MESSAGE_SPACING }}>
                {formatShortDateTime(createdAt)}
              </ChatSeparator>
            );
          }}
          renderMessageInput={getMessageInputDefinition({
            isLoading: exclusionQuery.isLoading || isLoadingChartingNoncompliances,
            actionBy: exclusionQuery.data?.[0]?.actionBy,
            sdk,
            workerHasChartingNonComplianceAtWorkplace,
            prefilledMessage,
          })}
          renderMessageContent={({ message, channel, userId, chainTop, chainBottom }) => (
            <MessageContentWithLongPress
              message={message}
              channel={channel!}
              userId={userId}
              chainTop={chainTop}
              chainBottom={chainBottom}
              renderMessageHeader={({ message }) => (
                <Text
                  variant="caption"
                  color="text.secondary"
                  sx={{ paddingY: 2, paddingX: isWorkplaceGroupChannel ? 5 : 0 }}
                >
                  {getSenderName(message as SendableMessageType, isWorkplaceGroupChannel)}
                </Text>
              )}
            />
          )}
          renderMessage={({ message }) => {
            if (hideMessage(message)) {
              return <></>;
            }

            if (isFacilityCanceledMeMessage(message)) {
              if (message.customType === CustomMessageTypes.FCM_REQUEST) {
                let facilityName: string | undefined;
                try {
                  ({ facilityName } = (message.data ? JSON.parse(message.data) : {}) as {
                    facilityName?: string;
                  });
                } catch {
                  // message.data is not valid JSON, fall through with undefined facilityName
                }
                // Overwrite message before rendering
                message.message = getSendHomeRequestMessage(facilityName);
              }
              // Render the default admin message
              return null as unknown as ReactElement;
            }

            if (message.customType === CustomMessageTypes.GROUP_SHIFT_INVITE_REQUEST) {
              return <GroupShiftInviteRequestMessage message={message} />;
            }

            if (message.customType === CustomMessageTypes.SHIFT_INVITE_REQUEST) {
              return <ShiftInviteRequestMessage message={message} />;
            }

            if (message.customType === CustomMessageTypes.VISIT_INVITE_REQUEST) {
              return <VisitInviteRequestMessage message={message} workplaceId={facilityId} />;
            }

            if (message.customType === CustomMessageTypes.WORKER_LOCK_NOTIFICATION) {
              return <WorkerVisitLockMessage message={message} lockedAt={lockedAt} />;
            }

            if (message.customType === CustomMessageTypes.SHIFT_TIME_PROPOSAL_CREATED) {
              return <ShiftTimeProposalMessage message={message} />;
            }

            if (
              message.customType === CustomMessageTypes.WORKER_CREDENTIAL_SHARED ||
              message.customType ===
                CustomMessageTypes.WORKER_CREDENTIAL_PROVISION_REQUEST_COMPLETED
            ) {
              return (
                <ChartingContextProvider workerId={worker.userId} workplaceId={facilityId}>
                  <WorkerCredentialSharedMessage
                    message={message}
                    workplaceId={facilityId}
                    workerId={worker.userId}
                  />
                </ChartingContextProvider>
              );
            }

            if (message.customType === CustomMessageTypes.SHIFT_BOOKED) {
              return <BookedShiftMessage message={message} />;
            }

            if (!message.customType && message.data) {
              try {
                const data = JSON.parse(message.data);

                const { sender } = message as SendableMessageType;
                if (sender && !sender.friendName) {
                  const chatTitle = `${data.workplaceUserName.split(" ")[0]} from ${
                    sender.nickname
                  }`;
                  sender.nickname = chatTitle;
                  sender.friendName = chatTitle;
                }
              } catch (error) {
                console.error(error);
              }
            }

            // We have to return undefined to render the sendbird message component and using any other value will make the message not visible on the UI.
            return null as unknown as ReactElement;
          }}
        />
      </ChatPageWrapper>
    </EditMessageProvider>
  );
}
/* eslint-enable max-lines -- TODO() */
